import {
  ApiDeliverySearchNFilterGridViewPostRequest,
  CodeNameRecord,
  Configuration,
  CreditMemoApi,
  CustomerApi,
  DeliveryApi,
  DeliveryModelODataResultCollection,
  DocumentFilterDimensions,
  ExportReportParams,
  FileUri,
  FileUriODataResultCollection,
  InvoiceApi,
  InvoiceModelODataResultCollection,
  OrderApi,
  OrderModelODataResultCollection,
  QuotationApi,
  QuotationModelODataResultCollection,
  ReportApi,
} from "api";

import { valueOr } from "util/statHelpers";
import {
  apiResponseTransformer,
  baseApiConfig,
  listOfFilters,
  PaginatedApiResponse,
  paginatedApiResponseTransformer,
  PER_PAGE,
  sortToString,
  TransformedApiResponse,
  validateApiResponse,
} from "./apiHelpers";
import { compare } from "./filterFactories";
import ModeOfTransportationService from "./ModeOfTransportationService";

type DocumentListFilterParams = {
  sort?: SortParameter;
  page?: number;
};

const createDocumentRequestParameters = ({
  sort,
  page = -1,
}: DocumentListFilterParams) => ({
  $orderby: sortToString(sort),
  $top: page >= 0 ? `${PER_PAGE}` : undefined,
  $skip: page >= 0 ? `${page * PER_PAGE}` : undefined,
  $expand: "DocumentLines",
});

class DocumentService {
  private quotationApi!: QuotationApi;
  private deliveryApi: DeliveryApi = new DeliveryApi();
  private orderApi!: OrderApi;
  private modes: Record<string, ModeOfTransportation> | undefined;
  private modeService!: ModeOfTransportationService;
  private invoiceApi!: InvoiceApi;
  private creditMemoApi!: CreditMemoApi;
  private customerApi!: CustomerApi;
  private reportApi!: ReportApi;

  constructor(config: Configuration = baseApiConfig) {
    this.quotationApi = new QuotationApi(config);
    this.deliveryApi = new DeliveryApi(config);
    this.orderApi = new OrderApi(config);
    this.modeService = new ModeOfTransportationService(config);
    this.invoiceApi = new InvoiceApi(config);
    this.creditMemoApi = new CreditMemoApi(config);
    this.customerApi = new CustomerApi(config);
    this.reportApi = new ReportApi(config);
  }

  private async fetchModes() {
    this.modes = {};
    return this.modeService.listModesOfTransportation().then(({ value }) =>
      value.forEach((mode) => {
        if (mode.code && this.modes) {
          this.modes[mode.code] = mode;
        }
      })
    );
  }

  getDocumentContractFilterDimensions(
    cardCodes: string[]
  ): Promise<DocumentFilterDimensions> {
    return this.quotationApi
      .apiQuotationGetDocumentFilterDimensionsGet({
        $filter: compare("CardCode").in(listOfFilters(cardCodes)),
      })
      .then((data) => data.data);
  }

  getDocumentOrderFilterDimensions(
    cardCodes: string[]
  ): Promise<DocumentFilterDimensions> {
    return this.orderApi
      .apiOrderGetDocumentFilterDimensionsGet({
        $filter: compare("CardCode").in(listOfFilters(cardCodes)),
      })
      .then((data) => data.data);
  }

  getDocumentDeliveriesFilterDimensions(
    cardCodes: string[]
  ): Promise<DocumentFilterDimensions> {
    return this.deliveryApi
      .apiDeliveryGetDocumentFilterDimensionsGet({
        $filter: compare("CardCode").in(listOfFilters(cardCodes)),
      })
      .then((data) => data.data);
  }

  getDocumentInvoiceFilterDimensions(
    cardCodes: string[]
  ): Promise<DocumentFilterDimensions> {
    return this.invoiceApi
      .apiInvoiceGetDocumentFilterDimensionsGet({
        $filter: compare("CardCode").in(listOfFilters(cardCodes)),
      })
      .then((data) => data.data);
  }

  getDocumentContractList(
    filterParams: DocumentListFilterParams,
    requestBody: ApiDeliverySearchNFilterGridViewPostRequest
  ): Promise<PaginatedApiResponse<QuotationModelODataResultCollection>> {
    return this.quotationApi
      .apiQuotationSearchNFilterGridViewPost({
        ...createDocumentRequestParameters(filterParams),
        apiDeliverySearchNFilterGridViewPostRequest: requestBody,
        $expand: "DocumentLines,ContractTotals",
      })
      .then(paginatedApiResponseTransformer(filterParams.page))
      .then(validateApiResponse("Document contracts failed to load"));
  }

  async getDocumentOrderList(
    filterParams: DocumentListFilterParams,
    requestBody: ApiDeliverySearchNFilterGridViewPostRequest
  ): Promise<PaginatedApiResponse<OrderModelODataResultCollection>> {
    if (!this.modes) {
      await this.fetchModes();
    }

    return this.orderApi
      .apiOrderSearchNFilterGridViewPost({
        ...createDocumentRequestParameters(filterParams),
        apiDeliverySearchNFilterGridViewPostRequest: requestBody,
      })
      .then(paginatedApiResponseTransformer(filterParams.page))
      .then(validateApiResponse("Document orders failed to load"));
  }

  getDeliveriesList(
    filterParams: DocumentListFilterParams,
    requestBody: ApiDeliverySearchNFilterGridViewPostRequest
  ): Promise<PaginatedApiResponse<DeliveryModelODataResultCollection>> {
    return this.deliveryApi
      .apiDeliverySearchNFilterGridViewPost({
        ...createDocumentRequestParameters(filterParams),
        apiDeliverySearchNFilterGridViewPostRequest: requestBody,
      })
      .then(paginatedApiResponseTransformer(filterParams.page))
      .then(validateApiResponse("Document deliveries failed to load"));
  }

  getDocumentInvoicesList(
    filterParams: DocumentListFilterParams,
    requestBody: ApiDeliverySearchNFilterGridViewPostRequest
  ): Promise<PaginatedApiResponse<InvoiceModelODataResultCollection>> {
    return this.invoiceApi
      .apiInvoiceSearchNFilterGridViewPost({
        ...createDocumentRequestParameters(filterParams),
        apiDeliverySearchNFilterGridViewPostRequest: requestBody,
      })
      .then(paginatedApiResponseTransformer(filterParams.page))
      .then(validateApiResponse("Document invoices failed to load"));
  }

  getDocumentFileList(
    cardCodes: string[]
  ): Promise<TransformedApiResponse<FileUriODataResultCollection, FileUri[]>> {
    return this.customerApi
      .apiCustomerGetFileUriListGet({
        $filter: compare("CardCode").in(listOfFilters(cardCodes)),
        $top: "50",
      })
      .then(apiResponseTransformer)
      .then(validateApiResponse("Files not found"))
      .then((data) => {
        return {
          ...data,
          value: data.value ?? [],
        };
      });
  }

  private static mapApiFileUri(fileUri: FileUri): DocumentsFile {
    return {
      category: valueOr(fileUri.Category),
      date: valueOr(fileUri.Date ? new Date(fileUri.Date) : undefined),
      filename: valueOr(fileUri.FileName),
      mimeType: valueOr(fileUri.Mime ?? "application/pdf"),
      url: fileUri.Uri ?? undefined,
    };
  }

  getDeliveriesFileList(
    docEntry: number
  ): Promise<
    TransformedApiResponse<FileUriODataResultCollection, DocumentsFile[]>
  > {
    const result = this.deliveryApi.apiDeliveryGetFileUriListDocEntryGet({
      docEntry,
      $top: "50",
    });
    return result
      .then(apiResponseTransformer)
      .then(validateApiResponse("Delivery Files not found"))
      .then((data) => {
        return {
          ...data,
          value: data.value?.map(DocumentService.mapApiFileUri) ?? [],
        };
      });
  }

  getFormattedCsv(exportReportParams: ExportReportParams) {
    return this.reportApi
      .apiReportDownloadPost({
        apiReportDownloadPostRequest: exportReportParams,
      })
      .then((response) => {
        if (!response) {
          throw new Error("Error in downloading CSV file");
        }

        // IRM's openapi file incorrectly says this API returns void
        return response.data as unknown as string;
      });
  }

  sendCsvEmail(exportReportParams: ExportReportParams) {
    return this.reportApi
      .apiReportSendEmailPost({
        apiReportDownloadPostRequest: exportReportParams,
      })
      .then((response) => {
        if (!response) {
          throw new Error("Error in sending CSV file");
        }

        return response.data;
      });
  }
  getValidCsvEmailRecipients(
    exportReportParams: ExportReportParams
  ): Promise<Array<CodeNameRecord>> {
    return this.reportApi
      .apiReportGetValidRecipientsPost({
        apiReportDownloadPostRequest: exportReportParams,
      })
      .then((response) => {
        if (!response) {
          throw new Error("Error in getting valid email recipients");
        }

        return response.data;
      });
  }
}

export default DocumentService;
